/**
 * Mount Sunny first-party attribution (storefront).
 * Phases: capture → cart attributes → dataLayer/gtag mirror (optional).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ms_attr_v1';
  var SESSION_INIT = 'ms_attr_sess_init';
  var TTL_MS = 90 * 24 * 60 * 60 * 1000;
  var MAX_KEY_PAGES = 25;
  var MAX_PRODUCTS = 15;
  var MAX_COLLECTIONS = 10;
  var MAX_BLOGS = 10;
  var MAX_SERVICES = 15;
  var MAX_JANE_URLS = 5;
  var CART_DEBOUNCE_MS = 800;
  var MAX_ATTR_JSON_CHARS = 900;
  var MAX_ORDER_STORY_CHARS = 480;

  var OWN_HOST = typeof location !== 'undefined' ? location.hostname : '';

  function debugEnabled() {
    try {
      return new URLSearchParams(location.search).get('ms_debug') === 'true';
    } catch (e) {
      return false;
    }
  }

  var dbg = debugEnabled();

  function log() {
    if (dbg && typeof console !== 'undefined' && console.log) {
      console.log.apply(console, ['[ms-attr]'].concat([].slice.call(arguments)));
    }
  }

  function now() {
    return Date.now();
  }

  function safeJsonParse(s, fallback) {
    try {
      return JSON.parse(s);
    } catch (e) {
      return fallback;
    }
  }

  function loadState() {
    var raw = null;
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return defaultState();
    }
    if (!raw) return defaultState();
    var o = safeJsonParse(raw, null);
    if (!o || o.v !== 1) return defaultState();
    if (o.expiresAt && now() > o.expiresAt) {
      var first = pickFirstTouch(o);
      o = defaultState();
      applyFirstTouch(o, first);
    }
    return o;
  }

  function pickFirstTouch(o) {
    return {
      first_touch_source: o.first_touch_source,
      first_touch_medium: o.first_touch_medium,
      first_touch_campaign: o.first_touch_campaign,
      first_touch_content: o.first_touch_content,
      first_touch_term: o.first_touch_term,
      first_landing_page: o.first_landing_page,
      first_referrer: o.first_referrer,
      first_visit_timestamp: o.first_visit_timestamp,
    };
  }

  function defaultState() {
    return {
      v: 1,
      expiresAt: now() + TTL_MS,
      first_touch_source: '',
      first_touch_medium: '',
      first_touch_campaign: '',
      first_touch_content: '',
      first_touch_term: '',
      first_landing_page: '',
      first_referrer: '',
      last_touch_source: '',
      last_touch_medium: '',
      last_touch_campaign: '',
      last_touch_content: '',
      last_touch_term: '',
      last_landing_page: '',
      last_referrer: '',
      visit_count: 0,
      first_visit_timestamp: 0,
      last_visit_timestamp: 0,
      pages_viewed_count: 0,
      key_pages: [],
      products_viewed: [],
      collections_viewed: [],
      blogs_viewed: [],
      service_pages_viewed: [],
      jane_booking_clicks: 0,
      jane_click_urls: [],
      add_to_cart_events: 0,
      first_add_to_cart_at: 0,
      intent: defaultIntent(),
    };
  }

  function defaultIntent() {
    return {
      viewed_acupuncture: false,
      viewed_facial_acupuncture: false,
      viewed_fertility: false,
      viewed_pregnancy: false,
      viewed_postpartum: false,
      viewed_tinctures: false,
      viewed_herbs: false,
      viewed_ear_seeds: false,
      viewed_blog: false,
      clicked_book_now: false,
      clicked_jane: false,
    };
  }

  function applyFirstTouch(o, first) {
    if (!first) return;
    Object.keys(first).forEach(function (k) {
      if (first[k]) o[k] = first[k];
    });
  }

  function saveState(o) {
    o.expiresAt = now() + TTL_MS;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(o));
    } catch (e) {}
  }

  function getSessionInit() {
    try {
      return sessionStorage.getItem(SESSION_INIT);
    } catch (e) {
      return '1';
    }
  }

  function setSessionInit() {
    try {
      sessionStorage.setItem(SESSION_INIT, '1');
    } catch (e) {}
  }

  function parseUtm() {
    var p = new URLSearchParams(location.search);
    return {
      utm_source: p.get('utm_source') || '',
      utm_medium: p.get('utm_medium') || '',
      utm_campaign: p.get('utm_campaign') || '',
      utm_content: p.get('utm_content') || '',
      utm_term: p.get('utm_term') || '',
    };
  }

  function inferFromReferrer(refUrl, hasGclid) {
    if (!refUrl) {
      return { source: 'direct', medium: 'none', campaign: '', content: '', term: '' };
    }
    var u;
    try {
      u = new URL(refUrl);
    } catch (e) {
      return { source: 'referral', medium: 'referral', campaign: '', content: '', term: '' };
    }
    var host = (u.hostname || '').replace(/^www\./, '').toLowerCase();
    if (host === OWN_HOST || host.endsWith('.' + OWN_HOST)) {
      return null;
    }
    if (/google\./.test(host)) {
      return {
        source: 'google',
        medium: hasGclid ? 'cpc' : 'organic',
        campaign: '',
        content: '',
        term: '',
      };
    }
    if (/bing\.com|duckduckgo\.com|yahoo\.com|ecosia\.org/.test(host)) {
      return { source: host.split('.')[0], medium: 'organic', campaign: '', content: '', term: '' };
    }
    if (host.indexOf('instagram.com') !== -1) {
      return { source: 'instagram', medium: 'social', campaign: '', content: '', term: '' };
    }
    if (host.indexOf('facebook.com') !== -1 || host.indexOf('fb.com') !== -1 || host.indexOf('l.facebook.com') !== -1) {
      return { source: 'facebook', medium: 'social', campaign: '', content: '', term: '' };
    }
    if (host.indexOf('pinterest.com') !== -1 || host.indexOf('pin.it') !== -1) {
      return { source: 'pinterest', medium: 'social', campaign: '', content: '', term: '' };
    }
    if (host.indexOf('tiktok.com') !== -1) {
      return { source: 'tiktok', medium: 'social', campaign: '', content: '', term: '' };
    }
    if (/mail\.google|outlook\.|yahoo\.|hotmail\.|gmail\.|icloud\.|fastmail|proton\.|hey\.com/.test(host)) {
      return { source: host, medium: 'email', campaign: '', content: '', term: '' };
    }
    return { source: host, medium: 'referral', campaign: '', content: '', term: '' };
  }

  function currentTouchSnapshot() {
    var utm = parseUtm();
    var hasUtm = !!(utm.utm_source || utm.utm_medium || utm.utm_campaign || utm.utm_content || utm.utm_term);
    var hasGclid = false;
    try {
      hasGclid = new URLSearchParams(location.search).has('gclid');
    } catch (e) {}
    if (hasUtm) {
      return {
        source: utm.utm_source || '(not set)',
        medium: utm.utm_medium || '(not set)',
        campaign: utm.utm_campaign || '',
        content: utm.utm_content || '',
        term: utm.utm_term || '',
        landing_page: location.pathname + location.search,
        referrer: document.referrer || '',
      };
    }
    var inf = inferFromReferrer(document.referrer, hasGclid);
    if (!inf) {
      return null;
    }
    return {
      source: inf.source,
      medium: inf.medium,
      campaign: inf.campaign,
      content: inf.content,
      term: inf.term,
      landing_page: location.pathname + location.search,
      referrer: document.referrer || '',
    };
  }

  function defaultDirectSnapshot() {
    return {
      source: 'direct',
      medium: 'none',
      campaign: '',
      content: '',
      term: '',
      landing_page: location.pathname + location.search,
      referrer: document.referrer || '',
    };
  }

  function touchEquals(a, b) {
    if (!a || !b) return false;
    return (
      a.source === b.source &&
      a.medium === b.medium &&
      a.campaign === b.campaign &&
      a.content === b.content &&
      a.term === b.term
    );
  }

  function applyTouchToFirst(o, snap) {
    if (!snap) return;
    if (o.first_touch_source) return;
    o.first_touch_source = snap.source;
    o.first_touch_medium = snap.medium;
    o.first_touch_campaign = snap.campaign;
    o.first_touch_content = snap.content;
    o.first_touch_term = snap.term;
    o.first_landing_page = snap.landing_page;
    o.first_referrer = snap.referrer;
    if (!o.first_visit_timestamp) o.first_visit_timestamp = now();
  }

  function applyTouchToLast(o, snap) {
    if (!snap) return;
    o.last_touch_source = snap.source;
    o.last_touch_medium = snap.medium;
    o.last_touch_campaign = snap.campaign;
    o.last_touch_content = snap.content;
    o.last_touch_term = snap.term;
    o.last_landing_page = snap.landing_page;
    o.last_referrer = snap.referrer;
  }

  function shouldUpdateLastTouch(o, snap) {
    if (!snap) return false;
    var prev = {
      source: o.last_touch_source,
      medium: o.last_touch_medium,
      campaign: o.last_touch_campaign,
      content: o.last_touch_content,
      term: o.last_touch_term,
    };
    if (!o.last_touch_source && !o.last_touch_medium) return true;
    return !touchEquals(prev, snap);
  }

  function capPush(arr, item, max) {
    arr.push(item);
    while (arr.length > max) arr.shift();
  }

  function dedupeBy(arr, keyFn) {
    var seen = {};
    var out = [];
    for (var i = arr.length - 1; i >= 0; i--) {
      var k = keyFn(arr[i]);
      if (!seen[k]) {
        seen[k] = true;
        out.unshift(arr[i]);
      }
    }
    return out;
  }

  function getPath() {
    return (location.pathname || '/').toLowerCase();
  }

  function classifyPage(path, templateName, pageHandle) {
    if (path === '/' || path === '') return { type: 'home', subtype: '' };
    if (path.indexOf('/cart') === 0) return { type: 'cart', subtype: '' };
    if (path.indexOf('/checkouts/') === 0 || path === '/checkout') {
      return { type: 'checkout', subtype: '' };
    }
    if (path.indexOf('/products/') === 0) return { type: 'product', subtype: '' };
    if (path.indexOf('/collections/') === 0) {
      var ch = path.replace(/^\/collections\//, '').split('/')[0];
      return { type: 'collection', subtype: ch };
    }
    if (path.indexOf('/blogs/') === 0) return { type: 'blog', subtype: '' };
    if (path.indexOf('/pages/') === 0) {
      var h = pageHandle || path.replace(/^\/pages\//, '').split('/')[0];
      return { type: 'service', subtype: h };
    }
    return { type: 'other', subtype: '' };
  }

  var SERVICE_HANDLES = {
    services: 1,
    acupuncture: 1,
    'acupuncture-cupping': 1,
    'facial-acupuncture': 1,
    'acupuncture-gua-sha-facial': 1,
    'holistic-microneedling': 1,
    'facial-marma-therapy': 1,
    massage: 1,
    shirodhara: 1,
    'cupping-massage': 1,
    'myofascial-facelift-massage': 1,
    visit: 1,
    hours: 1,
    'parking-directions': 1,
    faq: 1,
    'meet-the-team': 1,
    'start-here': 1,
    contact: 1,
  };

  function updateIntentFromPath(o, path, pageType, subtype) {
    var I = o.intent;
    var p = path;
    var h = (subtype || '').toLowerCase();

    if (pageType === 'blog' || p.indexOf('/blogs/') === 0) I.viewed_blog = true;

    if (p.indexOf('/collections/tincture') !== -1 || p.indexOf('tincture') !== -1) {
      I.viewed_tinctures = true;
    }
    if (p.indexOf('herb') !== -1 || p.indexOf('apothecary') !== -1 || p.indexOf('/collections/big-sun') !== -1) {
      I.viewed_herbs = true;
    }
    if (p.indexOf('ear-seed') !== -1 || p.indexOf('ear_seed') !== -1) {
      I.viewed_ear_seeds = true;
    }
    if (p.indexOf('fertility') !== -1 || h.indexOf('fertility') !== -1) I.viewed_fertility = true;
    if (p.indexOf('pregnancy') !== -1 || h.indexOf('pregnancy') !== -1) I.viewed_pregnancy = true;
    if (p.indexOf('postpartum') !== -1 || h.indexOf('postpartum') !== -1) I.viewed_postpartum = true;

    if (
      h === 'acupuncture' ||
      h === 'acupuncture-cupping' ||
      h === 'acupuncture-gua-sha-facial' ||
      h === 'cupping-massage' ||
      p.indexOf('/pages/acupuncture') !== -1
    ) {
      I.viewed_acupuncture = true;
    }
    if (h === 'facial-acupuncture' || h === 'acupuncture-gua-sha-facial' || p.indexOf('facial-acupuncture') !== -1) {
      I.viewed_facial_acupuncture = true;
    }

    if (pageType === 'service' && SERVICE_HANDLES[h]) {
      capPush(o.service_pages_viewed, { path: path, handle: h, t: now() }, MAX_SERVICES);
    }
  }

  function readPdpContext() {
    var el = document.getElementById('ms-pdp-context');
    if (!el || !el.textContent) return null;
    var d = safeJsonParse(el.textContent.trim(), null);
    return d && d.id ? d : null;
  }

  function emitAnalytics(eventName, payload) {
    payload = payload || {};
    log('emit', eventName, payload);
    if (typeof window.dataLayer !== 'undefined' && window.dataLayer && window.dataLayer.push) {
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
      if (eventName === 'ms_add_to_cart') {
        window.dataLayer.push(Object.assign({ event: 'add_to_cart_custom' }, payload));
      }
    }
    if (typeof window.gtag === 'function') {
      try {
        window.gtag('event', eventName, payload);
      } catch (e) {}
    }
  }

  var state = loadState();
  var prevIntent = JSON.stringify(state.intent);

  if (!getSessionInit()) {
    state.visit_count = (state.visit_count || 0) + 1;
    setSessionInit();
  }

  var snap = currentTouchSnapshot();
  if (!snap && !state.first_touch_source) {
    snap = defaultDirectSnapshot();
  }
  applyTouchToFirst(state, snap);
  if (snap && shouldUpdateLastTouch(state, snap)) {
    applyTouchToLast(state, snap);
  }

  state.last_visit_timestamp = now();
  if (!state.first_visit_timestamp) state.first_visit_timestamp = now();

  var path = getPath();
  var tmpl = typeof window.msAttrTemplate === 'string' ? window.msAttrTemplate : '';
  var pageHandle = typeof window.msAttrPageHandle === 'string' ? window.msAttrPageHandle : '';
  var classified = classifyPage(path, tmpl, pageHandle);
  if (tmpl === 'product') classified.type = 'product';

  state.pages_viewed_count = (state.pages_viewed_count || 0) + 1;
  capPush(state.key_pages, { path: path, type: classified.type, t: now() }, MAX_KEY_PAGES);

  var pdp = readPdpContext();
  if (classified.type === 'product' && pdp) {
    capPush(state.products_viewed, { id: pdp.id, handle: pdp.handle, title: pdp.title, t: now() }, MAX_PRODUCTS);
    state.products_viewed = dedupeBy(state.products_viewed, function (x) {
      return String(x.id);
    });
  }

  if (classified.type === 'collection') {
    capPush(
      state.collections_viewed,
      { path: path, handle: classified.subtype, t: now() },
      MAX_COLLECTIONS
    );
  }
  if (classified.type === 'blog') {
    capPush(state.blogs_viewed, { path: path, t: now() }, MAX_BLOGS);
  }

  updateIntentFromPath(state, path, classified.type, classified.subtype);

  var nextIntent = JSON.stringify(state.intent);
  if (nextIntent !== prevIntent) {
    try {
      var prev = safeJsonParse(prevIntent, {});
      var cur = state.intent;
      Object.keys(cur).forEach(function (k) {
        if (cur[k] && !prev[k]) {
          emitAnalytics('ms_intent_signal', { signal: k, path: path });
        }
      });
    } catch (e) {}
  }

  emitAnalytics('ms_page_view_enriched', {
    path: path,
    page_type: classified.type,
    visit_count: state.visit_count,
  });

  if (classified.type === 'product' && pdp) {
    emitAnalytics('ms_product_view', {
      product_id: String(pdp.id),
      handle: pdp.handle,
      title: pdp.title,
    });
  } else if (classified.type === 'blog') {
    emitAnalytics('ms_blog_view', { path: path });
  } else if (classified.type === 'service') {
    emitAnalytics('ms_service_view', { path: path, handle: pageHandle || classified.subtype });
  }

  saveState(state);

  var cartUpdateTimer = null;
  var cartMergeCache = null;
  var lastJaneClick = { href: '', t: 0 };
  var JANE_CLICK_DEDUPE_MS = 1000;
  var lastCheckoutClickAt = 0;
  var lastAtcEmit = { key: '', t: 0 };
  var ATC_DEDUPE_MS = 2500;

  function atcDedupeKey(payload) {
    var vid = payload && payload.variant_id ? String(payload.variant_id) : '';
    if (vid) return 'v:' + vid;
    if (payload && payload.title) return 't:' + String(payload.title);
    if (payload && payload.product_id) return 'p:' + String(payload.product_id);
    return '';
  }
  var CHECKOUT_CLICK_DEDUPE_MS = 1000;

  function cartUrls() {
    var t = window.theme && window.theme.routes;
    return {
      update: (t && t.cart_update_url) || '/cart/update.js',
      cart: '/cart.js',
    };
  }

  function summarizeForJson(o) {
    return {
      ft: {
        s: o.first_touch_source,
        m: o.first_touch_medium,
        c: o.first_touch_campaign,
        pg: o.first_landing_page,
      },
      lt: {
        s: o.last_touch_source,
        m: o.last_touch_medium,
        c: o.last_touch_campaign,
        pg: o.last_landing_page,
      },
      visits: o.visit_count,
      pvc: o.pages_viewed_count,
      jane: o.jane_booking_clicks,
      atc: o.add_to_cart_events,
      intent: o.intent,
      pv: (o.products_viewed || []).slice(-5),
      kp: (o.key_pages || []).slice(-8),
    };
  }

  function buildOrderStory(o) {
    var parts = [];
    parts.push('First touch: ' + (o.first_touch_source || '?') + ' / ' + (o.first_touch_medium || '?'));
    if (o.first_landing_page) parts.push('First page: ' + truncate(o.first_landing_page, 80));
    parts.push('Last touch: ' + (o.last_touch_source || '?') + ' / ' + (o.last_touch_medium || '?'));
    var titles = (o.products_viewed || [])
      .slice(-6)
      .map(function (x) {
        return x.title;
      })
      .filter(Boolean);
    if (titles.length) parts.push('Viewed products: ' + titles.join(', '));
    var svc = (o.service_pages_viewed || []).slice(-4).map(function (x) {
      return x.handle || x.path;
    });
    if (svc.length) parts.push('Service pages: ' + svc.join(', '));
    parts.push('Jane clicks: ' + (o.jane_booking_clicks || 0));
    parts.push('Add to carts: ' + (o.add_to_cart_events || 0));
    parts.push('Visits: ' + (o.visit_count || 0));
    var ttp = '';
    if (o.first_visit_timestamp && o.first_add_to_cart_at) {
      ttp = Math.round((o.first_add_to_cart_at - o.first_visit_timestamp) / 60000);
      parts.push('Time to first ATC (min): ' + ttp);
    }
    var s = parts.join(' | ');
    return truncate(s, MAX_ORDER_STORY_CHARS);
  }

  function truncate(s, n) {
    if (!s || s.length <= n) return s;
    return s.slice(0, n - 1) + '…';
  }

  function scheduleCartSync() {
    if (cartUpdateTimer) clearTimeout(cartUpdateTimer);
    cartUpdateTimer = setTimeout(syncCartAttributes, CART_DEBOUNCE_MS);
  }

  function syncCartAttributes() {
    cartUpdateTimer = null;
    var urls = cartUrls();
    fetch(urls.cart, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var attrs = (cart && cart.attributes) || {};
        var merged = Object.assign({}, attrs);
        merged.ms_order_story_source = buildOrderStory(state);
        var summary = summarizeForJson(state);
        var jsonStr = JSON.stringify(summary);
        if (jsonStr.length > MAX_ATTR_JSON_CHARS) {
          delete summary.kp;
          jsonStr = JSON.stringify(summary);
        }
        if (jsonStr.length > MAX_ATTR_JSON_CHARS) {
          delete summary.pv;
          jsonStr = JSON.stringify(summary);
        }
        merged.ms_attribution_json = truncate(jsonStr, MAX_ATTR_JSON_CHARS);

        return fetch(urls.update, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ attributes: merged }),
        });
      })
      .then(function (r) {
        if (dbg) log('cart attributes synced', r && r.status);
      })
      .catch(function (e) {
        if (dbg) log('cart sync err', e);
      });
  }

  function onJaneClick(href) {
    var ts = now();
    if (lastJaneClick.href === href && ts - lastJaneClick.t < JANE_CLICK_DEDUPE_MS) {
      if (dbg) log('jane click deduped', href);
      return;
    }
    lastJaneClick = { href: href, t: ts };
    state.jane_booking_clicks = (state.jane_booking_clicks || 0) + 1;
    state.intent.clicked_jane = true;
    state.intent.clicked_book_now = true;
    capPush(state.jane_click_urls, { url: href, t: now() }, MAX_JANE_URLS);
    saveState(state);
    emitAnalytics('ms_jane_booking_click', { link_url: truncate(href, 200), count: state.jane_booking_clicks });
    scheduleCartSync();
  }

  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var a = t.closest('a[href]');
      if (!a) return;
      var href = a.getAttribute('href') || '';
      if (!href) return;
      var low = href.toLowerCase();
      if (low.indexOf('janeapp.com') === -1 && low.indexOf('jane.app') === -1) return;
      onJaneClick(href);
    },
    true
  );

  function emitAddToCart(payload) {
    payload = payload || {};
    var key = atcDedupeKey(payload);
    var ts = now();
    if (key && lastAtcEmit.key === key && ts - lastAtcEmit.t < ATC_DEDUPE_MS) {
      if (dbg) log('atc deduped', key, payload.source);
      return false;
    }
    if (key) lastAtcEmit = { key: key, t: ts };
    emitAnalytics('ms_add_to_cart', payload);
    return true;
  }

  function recordAddToCartFromResponse(data, sourceTag) {
    var item = null;
    if (data && data.items && data.items.length) item = data.items[data.items.length - 1];
    else if (data && data.id) item = data;
    var payload = {
      source: sourceTag || 'unknown',
      product_id: item && item.product_id ? String(item.product_id) : '',
      variant_id:
        item && item.variant_id
          ? String(item.variant_id)
          : item && item.id
            ? String(item.id)
            : '',
      title: (item && item.title) || (item && item.product_title) || '',
    };
    if (!emitAddToCart(payload)) return;
    state.add_to_cart_events = (state.add_to_cart_events || 0) + 1;
    if (!state.first_add_to_cart_at) state.first_add_to_cart_at = now();
    saveState(state);
    scheduleCartSync();
  }

  document.body.addEventListener('shapes:modalcart:afteradditem', function (e) {
    var d = e.detail && e.detail.response;
    if (d) recordAddToCartFromResponse(d, 'ajax_modal');
  });

  document.body.addEventListener('shapes:cart:adderror', function (e) {
    if (dbg) log('cart add error', e.detail);
  });

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || !form.getAttribute) return;
      var action = (form.getAttribute('action') || '').toLowerCase();
      if (action.indexOf('/cart/add') === -1) return;
      var modalCart =
        window.theme && window.theme.settings && window.theme.settings.cart_type === 'modal';
      if (modalCart) return;
      var vid = '';
      try {
        var idInput = form.querySelector('[name="id"]');
        vid = idInput ? idInput.value : '';
      } catch (err) {}
      if (
        !emitAddToCart({
          source: 'form_submit',
          variant_id: String(vid),
        })
      ) {
        return;
      }
      state.add_to_cart_events = (state.add_to_cart_events || 0) + 1;
      if (!state.first_add_to_cart_at) state.first_add_to_cart_at = now();
      saveState(state);
      scheduleCartSync();
    },
    true
  );

  (function patchFetch() {
    var modal = window.theme && window.theme.settings && window.theme.settings.cart_type === 'modal';
    if (modal) return;
    if (typeof window.fetch !== 'function') return;
    var _fetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url;
      var isAdd = url && url.indexOf('/cart/add') !== -1;
      return _fetch.apply(this, arguments).then(function (res) {
        if (!isAdd || !res.ok) return res;
        var clone = res.clone();
        clone
          .json()
          .then(function (data) {
            if (!data || data.status) return;
            recordAddToCartFromResponse(data, 'fetch');
          })
          .catch(function () {});
        return res;
      });
    };
  })();

  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var a = t.closest('a[href]');
      if (!a) return;
      var href = (a.getAttribute('href') || '').toLowerCase();
      if (href.indexOf('/checkout') === -1 && href.indexOf('/checkouts/') === -1) return;
      var ts = now();
      if (ts - lastCheckoutClickAt < CHECKOUT_CLICK_DEDUPE_MS) {
        if (dbg) log('checkout click deduped', href);
        return;
      }
      lastCheckoutClickAt = ts;
      emitAnalytics('ms_checkout_started', { path: location.pathname });
      scheduleCartSync();
    },
    true
  );

  scheduleCartSync();

  window.MountSunnyAttribution = {
    getState: function () {
      return JSON.parse(JSON.stringify(state));
    },
    flushCartAttributes: syncCartAttributes,
  };
})();
